
import { useState, useEffect, useMemo } from "react";
import { UserProfile, UserRole } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import UserRoleSelector from "./UserRoleSelector";
import PaginatedTable from "@/components/common/PaginatedTable";
import { usePagination } from "@/hooks/usePagination";

interface UsersTableProps {
  users: UserProfile[];
  filteredUsers: UserProfile[];
  isLoading: boolean;
  updateUserRole: (userId: string, newRole: UserRole) => Promise<void>;
}

const UsersTable = ({ users, filteredUsers, isLoading, updateUserRole }: UsersTableProps) => {
  // Use the pagination hook for more efficient pagination
  const { 
    currentPage, 
    pageSize, 
    setPage, 
    setPageSize
  } = usePagination({
    initialPage: 1,
    initialPageSize: 10,
    pageSizeOptions: [5, 10, 20, 50, 100]
  });
  
  // Use useMemo to avoid unnecessary recalculations when other state changes
  const totalItems = useMemo(() => filteredUsers.length, [filteredUsers]);
  
  // Efficiently calculate current items only when dependencies change
  const currentItems = useMemo(() => {
    const indexOfLastItem = currentPage * pageSize;
    const indexOfFirstItem = indexOfLastItem - pageSize;
    return filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredUsers, currentPage, pageSize]);
  
  // Reset to first page when filtered results change
  useEffect(() => {
    setPage(1);
  }, [filteredUsers.length, setPage]);
  
  // Define columns for the table
  const columns = useMemo(() => [
    { key: 'email', header: 'Email' },
    { 
      key: 'role', 
      header: 'Role',
      render: (user: UserProfile) => (
        <UserRoleSelector 
          currentRole={user.role}
          userId={user.id}
          onRoleChange={updateUserRole}
        />
      )
    },
    { 
      key: 'created_at', 
      header: 'Created At',
      render: (user: UserProfile) => new Date(user.created_at).toLocaleDateString()
    },
    { 
      key: 'actions', 
      header: 'Actions',
      render: (user: UserProfile) => (
        <div className="text-right">
          <Button variant="ghost" size="sm">
            Edit
          </Button>
        </div>
      )
    }
  ], [updateUserRole]);

  return (
    <PaginatedTable
      data={currentItems}
      columns={columns}
      totalItems={totalItems}
      currentPage={currentPage}
      itemsPerPage={pageSize}
      onPageChange={setPage}
      onItemsPerPageChange={setPageSize}
      isLoading={isLoading}
      emptyMessage={
        users.length === 0 
          ? "No users found. Click the Refresh button to load users."
          : "No users match your search."
      }
    />
  );
};

export default UsersTable;
